import { NormalizeApiError, setSentryErrorTags } from "@app/api/BaseAPI";
import {
	DeleteCapsuleEntry,
	PostCapsuleEntry,
	PutCapsuleEntry,
} from "@app/api/CapsuleEntries";
import { PutCapsuleProjects } from "@app/api/CapsuleProjects";
import {
	ClearTiming,
	NewProjectTimeForDelete,
	NewProjectTimeForEdit,
	type ProjectTime,
} from "@app/components/Timing";
import type { EntryForDisplay } from "@app/interfaces/CapsuleEntry";
import type { ProjectSummary } from "@app/interfaces/ProjectSummary";
import type { UserDetails } from "@app/interfaces/UserDetails";
import * as Sentry from "@sentry/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const useEntryMutations = () => {
	const queryClient = useQueryClient();

	const postEntryQuery = useMutation({
		mutationFn: async (p: {
			timing: ProjectTime;
			project: ProjectSummary;
			user: UserDetails;
			onSuccess?: () => void;
		}) => {
			await PostCapsuleEntry(p.timing, p.project.project_id, p.user);
			await PutCapsuleProjects(p.timing, p.project);
		},
		onSuccess: async (_, p) => {
			await queryClient.invalidateQueries({ queryKey: ["projects"] });
			await queryClient.invalidateQueries({
				queryKey: ["project", p.project.id.toFixed(0)],
			});
			await queryClient.invalidateQueries({
				queryKey: ["entries", p.project.id],
			});
			await queryClient.invalidateQueries({
				queryKey: ["people", p.project.id],
			});

			ClearTiming(p.project.project_id);
			p.onSuccess?.();
		},
		onError: (err, p) => {
			const apiErr = NormalizeApiError(err);

			Sentry.withScope((scope) => {
				scope.setFingerprint(["post", "/entries"]);
				setSentryErrorTags(scope, apiErr);
				scope.setExtra("input", JSON.stringify(p.timing));
				Sentry.captureException(apiErr.captureError);
			});

			alert(
				`Something went wrong (${
					apiErr.status != null ? apiErr.status : "Unknown Status Code"
				}), we have not cleared your timer so you can try again.`,
			);
		},
	});

	const putEntryQuery = useMutation({
		mutationFn: async (p: {
			original: EntryForDisplay;
			data: EntryForDisplay;
			project: ProjectSummary;
			onSuccess?: () => void;
		}) => {
			await PutCapsuleEntry(p.original.id, p.data);
			await PutCapsuleProjects(
				NewProjectTimeForEdit(
					p.project.id,
					p.data.minutes - p.original.minutes,
				),
				p.project,
			);
		},
		onSuccess: async (_, p) => {
			await queryClient.invalidateQueries({ queryKey: ["projects"] });
			await queryClient.invalidateQueries({
				queryKey: ["project", p.project.id.toFixed(0)],
			});
			await queryClient.invalidateQueries({
				queryKey: ["entries", p.project.id],
			});
			await queryClient.invalidateQueries({
				queryKey: ["people", p.project.id],
			});

			ClearTiming(p.project.project_id);
			p.onSuccess?.();
		},
		onError: (err, p) => {
			const apiErr = NormalizeApiError(err);

			Sentry.withScope((scope) => {
				scope.setFingerprint(["put", "/entries"]);
				setSentryErrorTags(scope, apiErr);
				scope.setExtra("input", JSON.stringify(p.data));
				Sentry.captureException(apiErr.captureError);
			});

			alert(
				`Something went wrong (${
					apiErr.status != null ? apiErr.status : "Unknown Status Code"
				}), please try again.`,
			);
		},
	});

	const deleteEntryQuery = useMutation({
		mutationFn: async (p: {
			timeTrack: EntryForDisplay;
			project: ProjectSummary;
			onSuccess?: () => void;
		}) => {
			await DeleteCapsuleEntry(p.timeTrack.id);
			await PutCapsuleProjects(
				NewProjectTimeForDelete(p.project.id, p.timeTrack.minutes),
				p.project,
			);
		},
		onSuccess: async (_, p) => {
			await queryClient.invalidateQueries({ queryKey: ["projects"] });
			await queryClient.invalidateQueries({
				queryKey: ["project", p.project.id.toFixed(0)],
			});
			await queryClient.invalidateQueries({
				queryKey: ["entries", p.project.id],
			});
			await queryClient.invalidateQueries({
				queryKey: ["people", p.project.id],
			});

			ClearTiming(p.project.project_id);
			p.onSuccess?.();
		},
		onError: (err, p) => {
			const apiErr = NormalizeApiError(err);

			Sentry.withScope((scope) => {
				scope.setFingerprint(["delete", "/entries"]);
				setSentryErrorTags(scope, apiErr);
				scope.setExtra("input", JSON.stringify(p.timeTrack));
				Sentry.captureException(apiErr.captureError);
			});

			alert(
				`Something went wrong (${
					apiErr.status != null ? apiErr.status : "Unknown Status Code"
				}), please try again.`,
			);
		},
	});

	return {
		postEntryQuery,
		putEntryQuery,
		deleteEntryQuery,
	};
};
